const DEFAULT_KIOSK_TIMEOUT_MS = 45_000;
const DEFAULT_QR_EXPIRY_MS = 120_000;
const DEFAULT_LOGOUT_SESSION_MS = 45_000;

export { DEFAULT_KIOSK_TIMEOUT_MS };

const DEFAULT_SETTINGS = {
  milliseconds: DEFAULT_KIOSK_TIMEOUT_MS,
  qrExpiryMs: DEFAULT_QR_EXPIRY_MS,
  logoutSessionMs: DEFAULT_LOGOUT_SESSION_MS,
};

export type KioskTimeoutSettings = typeof DEFAULT_SETTINGS;

let cachedSettings: KioskTimeoutSettings | null = null;
let inflight: Promise<KioskTimeoutSettings> | null = null;

const stripEnvQuotes = (value: string | undefined) =>
  (value || "").trim().replace(/^['"]|['"]$/g, "").replace(/\/+$/, "");

const getApiBaseUrl = () =>
  stripEnvQuotes(
    process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_BASE_URL
  );

function parseMilliseconds(value: unknown) {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.round(value);
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  return null;
}

function extractSettingsRecord(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== "object") return null;
  const root = payload as Record<string, unknown>;
  if (
    parseMilliseconds(root.milliseconds) ||
    parseMilliseconds(root.qr_expiry) ||
    parseMilliseconds(root.logout_session)
  ) {
    return root;
  }

  const data = root.data;
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  if (
    parseMilliseconds(record.milliseconds) ||
    parseMilliseconds(record.qr_expiry) ||
    parseMilliseconds(record.logout_session)
  ) {
    return record;
  }

  if (record.data && typeof record.data === "object") {
    return record.data as Record<string, unknown>;
  }
  return null;
}

export function parseKioskTimeoutSettings(payload: unknown): KioskTimeoutSettings {
  const record = extractSettingsRecord(payload);
  return {
    milliseconds:
      parseMilliseconds(record?.milliseconds) || DEFAULT_KIOSK_TIMEOUT_MS,
    qrExpiryMs: parseMilliseconds(record?.qr_expiry) || DEFAULT_QR_EXPIRY_MS,
    logoutSessionMs:
      parseMilliseconds(record?.logout_session) || DEFAULT_LOGOUT_SESSION_MS,
  };
}

async function loadSettings() {
  const apiBaseUrl = getApiBaseUrl();
  if (!apiBaseUrl) {
    console.warn(
      "[kioskTimeoutService] API base URL is not configured. Set REACT_APP_BASE_URL."
    );
    return DEFAULT_SETTINGS;
  }

  try {
    const response = await fetch(`${apiBaseUrl}/kiosk-timeout-setting`, {
      headers: { accept: "*/*" },
    });
    if (!response.ok) return DEFAULT_SETTINGS;
    return parseKioskTimeoutSettings(await response.json());
  } catch (error) {
    console.warn("[kioskTimeoutService] Failed to load timeout:", error);
    return DEFAULT_SETTINGS;
  }
}

export async function fetchKioskTimeoutSettings() {
  if (cachedSettings) return cachedSettings;
  if (!inflight) {
    inflight = loadSettings().finally(() => {
      inflight = null;
    });
  }
  cachedSettings = await inflight;
  return cachedSettings;
}
